import { z } from 'zod'
import type { IncomingMessage } from 'http'
import type { ConfigParameters } from '@auth0/nextjs-auth0'

import {
  getPlatformBaseUrlFromRequest,
  getOpCoFromRequest,
} from '@reward-platform/platform-config/server'
import { isString } from '@reward-platform/tsutil'
import { ZODException } from '@reward-platform/shared-api/exceptions/ZODException'
import { sanitizeBaseUrl } from '@reward-platform/utils'
import logger from '@reward-platform/logger/server/next'

import { getAuthConfigForOpCo } from '../../config'
import { AUTH_PATHS } from '../../utils/authPaths'

const COMMA_SEPARATOR = ','
const SPACE_SEPARATOR = ' '

const Auth0ParametersSchema = z.object({
  secret: z.string(),
  baseURL: z.string(),
  issuerBaseURL: z.preprocess((value: unknown) => {
    if (isString(value)) {
      return sanitizeBaseUrl(value)
    }
    return value
  }, z.string()),
  clientID: z.string(),
  clientSecret: z.string(),
  authorizationParams: z.object({
    audience: z.string().min(1),
    redirect_uri: z.string(),
    scope: z.preprocess(
      (value: unknown) =>
        isString(value) ? value.split(COMMA_SEPARATOR).join(SPACE_SEPARATOR) : value,
      z.string()
    ),
  }),
  cacheLocation: z.union([z.literal('memory'), z.literal('localstorage')]).default('memory'),
})

export const getOpCoAuth0Config = (req: IncomingMessage) => {
  const iataCode = getOpCoFromRequest(req)
  const opCoAuthConfig = getAuthConfigForOpCo(iataCode)
  const baseURL = getPlatformBaseUrlFromRequest(req, false)
  const redirectUri = `${getPlatformBaseUrlFromRequest(req)}${AUTH_PATHS.CALLBACK}`

  const rawConfig: ConfigParameters = {
    secret: opCoAuthConfig.clientSecret,
    baseURL,
    issuerBaseURL: opCoAuthConfig.issuer,
    clientID: opCoAuthConfig.clientId,
    clientSecret: opCoAuthConfig.clientSecret,
    authorizationParams: {
      audience: opCoAuthConfig.audience,
      scope: opCoAuthConfig.scope,
      cacheLocation: opCoAuthConfig.cacheLocation,
      redirect_uri: redirectUri,
    },
  }

  const parsedConfig = Auth0ParametersSchema.safeParse(rawConfig)
  if (!parsedConfig.success) {
    const error = new ZODException(parsedConfig.error)
    logger.error(
      `Auth0 Config is not setup correctly for partner "${iataCode}"`,
      error.formatErrorForLogs('auth_b2c')
    )
    throw error
  }
  return { iataCode, config: parsedConfig.data }
}
